import type { ComponentType } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { DEFAULT_SECTION, ROUTES } from '@/shared/config';
import { GuestOnly, MarketGate, RequireAuth } from './guards';
import { RouteError } from './RouteError';

/**
 * Маршруты админки (deltas.md): `/login`; защищённая зона с оболочкой — `/:section`,
 * `/:section/:id?tab=…`. Страницы грузятся лениво (ui-and-design.md: чанк страницы ≤ 500 KB).
 */
/** Реестр и карточка раздела с ленивой страницей. */
function sectionRoutes(section: string, load: () => Promise<ComponentType>): RouteObject[] {
  const lazy = async () => ({ Component: await load() });
  return [
    { path: section, lazy },
    { path: `${section}/:id`, lazy },
  ];
}

export function createAppRoutes(): RouteObject[] {
  return [
    {
      element: <MarketGate />,
      errorElement: <RouteError />,
      children: [
        {
          element: <GuestOnly />,
          children: [
            {
              path: ROUTES.login,
              lazy: async () => ({ Component: (await import('@/pages/login')).LoginPage }),
            },
          ],
        },
        {
          element: <RequireAuth />,
          children: [
            { index: true, element: <Navigate to={ROUTES.section(DEFAULT_SECTION)} replace /> },
            // Разделы с собственной страницей (статичный путь важнее `:section`); страница сама
            // различает реестр `/:section` и карточку `/:section/:id`.
            {
              path: 'dashboard',
              lazy: async () => ({ Component: (await import('@/pages/dashboard')).DashboardPage }),
            },
            {
              path: 'profile',
              lazy: async () => ({ Component: (await import('@/pages/profile')).ProfilePage }),
            },
            ...sectionRoutes('users', async () => (await import('@/pages/users')).UsersPage),
            ...sectionRoutes('team', async () => (await import('@/pages/team')).TeamPage),
            ...sectionRoutes('care', async () => (await import('@/pages/care')).CarePage),
            ...sectionRoutes(
              'countries',
              async () => (await import('@/pages/countries')).CountriesPage,
            ),
            {
              path: 'origins',
              lazy: async () => ({ Component: (await import('@/pages/origins')).OriginsPage }),
            },
            ...sectionRoutes('regions', async () => (await import('@/pages/regions')).RegionsPage),
            ...sectionRoutes(
              'wineries',
              async () => (await import('@/pages/wineries')).WineriesPage,
            ),
            ...sectionRoutes(
              'appmarkets',
              async () => (await import('@/pages/appmarkets')).AppMarketsPage,
            ),
            ...sectionRoutes('wines', async () => (await import('@/pages/wines')).WinesPage),
            ...sectionRoutes('sets', async () => (await import('@/pages/sets')).SetsPage),
            ...sectionRoutes('drops', async () => (await import('@/pages/drops')).DropsPage),
            ...sectionRoutes(
              'tastings',
              async () => (await import('@/pages/experiences')).TastingsPage,
            ),
            ...sectionRoutes(
              'excursions',
              async () => (await import('@/pages/experiences')).ExcursionsPage,
            ),
            ...sectionRoutes('trips', async () => (await import('@/pages/experiences')).TripsPage),
            ...sectionRoutes('orders', async () => (await import('@/pages/orders')).OrdersPage),
            ...sectionRoutes(
              'partners',
              async () => (await import('@/pages/partners')).PartnersPage,
            ),
            ...sectionRoutes(
              'deliveries',
              async () => (await import('@/pages/deliveries')).DeliveriesPage,
            ),
            ...sectionRoutes('revenue', async () => (await import('@/pages/revenue')).RevenuePage),
            ...sectionRoutes(
              'bookings',
              async () => (await import('@/pages/bookings')).BookingsPage,
            ),
            ...['tastelog', 'excurlog', 'triplog'].map((path) => ({
              path,
              lazy: async () => ({
                Component: (await import('@/pages/attendance')).AttendancePage,
              }),
            })),
            ...sectionRoutes('courses', async () => (await import('@/pages/learning')).CoursesPage),
            ...sectionRoutes('lessons', async () => (await import('@/pages/learning')).LessonsPage),
            ...sectionRoutes('tests', async () => (await import('@/pages/learning')).TestsPage),
            {
              path: 'learnlog',
              lazy: async () => ({ Component: (await import('@/pages/learning')).LearnLogPage }),
            },
            ...sectionRoutes(
              'publications',
              async () => (await import('@/pages/content')).PublicationsPage,
            ),
            ...sectionRoutes('stories', async () => (await import('@/pages/content')).StoriesPage),
            ...sectionRoutes('docs', async () => (await import('@/pages/content')).DocumentsPage),
            {
              path: 'brand',
              lazy: async () => ({ Component: (await import('@/pages/content')).BrandPage }),
            },
            {
              path: 'sublog',
              lazy: async () => ({
                Component: (await import('@/pages/content')).SubscriptionLogPage,
              }),
            },
            {
              path: 'likelog',
              lazy: async () => ({ Component: (await import('@/pages/content')).LikeLogPage }),
            },
            ...sectionRoutes(
              'calibs',
              async () => (await import('@/pages/taste')).CalibrationsPage,
            ),
            {
              path: 'accuracy',
              lazy: async () => ({
                Component: (await import('@/pages/taste')).AccuracyLevelsPage,
              }),
            },
            {
              path: 'specs',
              lazy: async () => ({
                Component: (await import('@/pages/taste')).SpecializationsPage,
              }),
            },
            {
              path: 'acclog',
              lazy: async () => ({ Component: (await import('@/pages/taste')).AccuracyLogPage }),
            },
            {
              path: 'speclog',
              lazy: async () => ({
                Component: (await import('@/pages/taste')).SpecializationLogPage,
              }),
            },
            ...sectionRoutes('diarylog', async () => (await import('@/pages/taste')).DiaryLogPage),
            ...sectionRoutes('xp', async () => (await import('@/pages/gamification')).XpRulesPage),
            {
              path: 'levels',
              lazy: async () => ({
                Component: (await import('@/pages/gamification')).LevelsPage,
              }),
            },
            {
              path: 'rewards',
              lazy: async () => ({
                Component: (await import('@/pages/gamification')).RewardsPage,
              }),
            },
            {
              path: 'tiers',
              lazy: async () => ({ Component: (await import('@/pages/gamification')).TiersPage }),
            },
            {
              path: 'xplog',
              lazy: async () => ({ Component: (await import('@/pages/gamification')).XpLogPage }),
            },
            {
              path: 'stamplog',
              lazy: async () => ({
                Component: (await import('@/pages/gamification')).StampLogPage,
              }),
            },
            ...sectionRoutes(
              'notifs',
              async () => (await import('@/pages/notifications')).NotificationsPage,
            ),
            ...sectionRoutes(
              'moderation',
              async () => (await import('@/pages/moderation')).ModerationPage,
            ),
            ...sectionRoutes('audit', async () => (await import('@/pages/audit')).AuditPage),
            ...sectionRoutes(
              'onboarding',
              async () => (await import('@/pages/onboarding')).OnboardingPage,
            ),
            ...sectionRoutes(
              'integrations',
              async () => (await import('@/pages/integrations')).IntegrationsPage,
            ),
            ...sectionRoutes(
              'integration_logs',
              async () => (await import('@/pages/integration-logs')).IntegrationLogsPage,
            ),
            {
              path: ':section',
              lazy: async () => ({ Component: (await import('@/pages/section')).SectionPage }),
            },
            {
              path: ':section/:id',
              lazy: async () => ({ Component: (await import('@/pages/section')).SectionPage }),
            },
            {
              path: '*',
              lazy: async () => ({ Component: (await import('@/pages/not-found')).NotFoundPage }),
            },
          ],
        },
      ],
    },
  ];
}
